import { forEach, filter, every, last } from 'lodash';

import { resolve } from 'gear';

import { Brush, DisplayPen, Color } from '../style';

import { Figures, Group, PowerClipContent } from '../figure';

import { Layer } from './layer';

const editContentFigureAlpha = 0.6;

export const ContentLayer = Layer.ContentLayer = Layer.extend({ typeName: 'ContentLayer',
  init( config ) {
    this.callParent(config);
    this.metadata.set('contentType',this._type);
  }
});

const ContentLayerType = function(figure/*,point*/) {
  if( figure.isGroup ) {
    return ContentLayer.Group;
  }
  else if( figure.isText && figure.isMultiPart ) {
    return ContentLayer.MultiPartText;
  }
  else if( figure._powerClipContents.length > 0 ) {
    return ContentLayer.PowerClip;
  }
  else if( figure.isShape ) {
    return ContentLayer.Shape;
  }
  return null;
};

ContentLayer.extract = function(original,figure,point ) {
  return ContentLayerType(figure,point).extract(figure,point);
};


ContentLayer.Background = ContentLayer.extend({ typeName: 'BackgroundContentLayer',
  _type: 'Background'
});

// Background
//------------------------------------------------------------------------------

ContentLayer.FigureBackground = ContentLayer.extend({ typeName: 'FigureBackgroundContentLayer',
  _type: 'FigureBackground',

  // [TODO] This should go to \ui
  beforeDraw( context, config ) {
    if( config.editingContent ) {
      context.globalAlpha *= editContentFigureAlpha;
    }
  },
  afterDraw( context, config ) {
    if( config.editingContent ) {
      context.globalAlpha /= editContentFigureAlpha;
    }
  }
});


ContentLayer.Figure = ContentLayer.extend({ typeName: 'FigureContentLayer',
  beforeDraw( /* context, config */) {
    // Leave as is...
  },
  afterDraw( /* context, config */) {
    // Leave as is...
  },
  initialSelectedFigures( /*point*/) {
    return this._figures;
  }
});

ContentLayer.Foreground = ContentLayer.extend({ typeName: 'ForegroundContentLayer',
  _type: 'Foreground'
});

// Editing Content
//------------------------------------------------------------------------------

function simpleContent( ContentLayerType, content ) {
  const main = new ContentLayerType({ figures: content });
  return { layers: [ main ], main };
}

function groupedSelectedFigures( point ) {
  const { figures } = this;
  return [ ( point && Figures.figureAtPoint_(figures,point) ) || last(figures) ];
}

const preserveProperties = function(newFigure,originalFigure) {
  forEach([ 'id', 'name', 'alpha', 'metadata' ], prop => {
    newFigure[prop](originalFigure[prop]());
  });
  return newFigure;
};

ContentLayer.Group = ContentLayer.Figure.extend({ typeName: 'GroupContentLayer',

  _type: 'Group',

  assemble$( originalFigure) {
    const { figures } = this;
    return resolve( figures.length === 0 ? null :
      originalFigure.regroup( figures )
    );
  },

  initialSelectedFigures: groupedSelectedFigures,

  statusMessage() {
    return 'Group';
  }
});

ContentLayer.Group.extract = function(figure/*,point*/) {
  return simpleContent( ContentLayer.Group, figure.ungroup() );
};

ContentLayer.PowerClip = ContentLayer.Figure.extend({ typeName: 'PowerClipContentLayer',

  _type: 'PowerClip',

  assemble$( originalFigure) {
    // [TODO] Add supoort for multiple power clip contents
    const { powerClipContents } = originalFigure;
    const pcc = powerClipContents[0];

    const { figures } = this;
    if( figures.length > 0 ) {
      const pccN = PowerClipContent.createForFigure('id', figures, originalFigure);
      pcc.figures.set(pccN.figures.clear());
    }
    else {
      powerClipContents.remove(pcc);
    }

    return resolve(originalFigure);
  },

  initialSelectedFigures( point ) {
    const { figures } = this;
    if( point ) {
      const figure = Figures.figureAtPoint_(figures,point);
      return figure ? [figure] : figures;
    }
    else {
      return figures;
    }
  },

  statusMessage() {
    return 'Power Clip Content';
  }
});
ContentLayer.PowerClip.extract = function( figure/*, point*/ ) {
  const pcc = figure._powerClipContents[0],
        content = pcc.extractFigures(),
        cf = figure.clone(),
        background = [ cf ];

  if( figure.isText ) {
    if( figure.powerClipId === pcc._id ) {
      const fc = cf.clone();
      fc.brush = new Brush({ color: Color.Transparent() });
      fc.pen   = new DisplayPen();
      background.push( fc );
    }
  }
  else if( figure.isShape ) {
    const { polyregions } = cf;
    forEach( filter(polyregions,'powerClipId',pcc.id), p => {
      const pc = p.clone();
      pc.brush = new Brush({ color: Color.Transparent() });
      pc.pen   = new DisplayPen();
      polyregions.insert(polyregions.indexOf(p),pc);
    });
  }

  const backgroundLayer = new ContentLayer.FigureBackground({ figures: background });
  const main = new ContentLayer.PowerClip({ figures: content });
  return { layers: [ backgroundLayer, main ], main };
};

ContentLayer.MultiPartText = ContentLayer.Figure.extend({ typeName: 'MultiPartTextContentLayer',

  _type: 'MultiPartText',

  assemble$( originalFigure ) {
    const { figures } = this;

    // [TODO] We can flatten the multi part text and regroup here
    return figures.length === 0 ? resolve(null) :

      every(figures, f => f.isText && ! f.isMultiPart ) ?

      originalFigure.regroupParts$(figures) :

      // Fallback to normal grouping
      resolve( preserveProperties( Group.create(figures), originalFigure ) );
  },

  initialSelectedFigures: groupedSelectedFigures,

  statusMessage() {
    return 'Multi Part Text';
  }
});
ContentLayer.MultiPartText.extract = function( figure/*, point*/ ) {
  return simpleContent( ContentLayer.MultiPartText, figure.breakIntoParts() );
};


ContentLayer.Shape = ContentLayer.Figure.extend({ typeName: 'ShapeContentLayer',

  _type: 'Shape',

  assemble$( originalFigure) {
    const { figures } = this;
    return resolve( figures.length === 0 ? resolve(null) :

      every(figures,'isShape') ?

       // Reconstruct the shape
       originalFigure.regroupShapes( figures ) :

       // Resort to group to be able to include text and rasters
       preserveProperties( Group.create(figures), originalFigure )
    );
  },

  initialSelectedFigures: groupedSelectedFigures,

  statusMessage() {
    return 'Shape';
  }
});
ContentLayer.Shape.extract = function( figure/*, point*/ ) {
  return simpleContent( ContentLayer.Shape, figure.breakApartPolyregions() );
};



/** WEBPACK FOOTER **
 ** ./src/cdl/document/content-layer.js
 **/